import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  Building2, 
  Users, 
  TrendingUp,
  Globe,
  Sparkles,
  FileText,
  BarChart3,
  MessageSquare,
  Target,
  Clock,
  Zap
} from "lucide-react";

interface AuditProcessProps {
  submissionId: string;
  onComplete: () => void;
  onCancel: () => void;
}

interface ProcessStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'success' | 'error';
  data?: any;
  error?: string;
}

export const SocialSellingAuditProcess = ({ submissionId, onComplete, onCancel }: AuditProcessProps) => {
  const { toast } = useToast();
  const [submission, setSubmission] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<ProcessStep[]>([
    { id: 'init', label: 'Inicjalizacja', status: 'pending' },
    { id: 'competitors', label: 'Identyfikacja konkurencji', status: 'pending' },
    { id: 'linkedin_scraping', label: 'Zbieranie danych LinkedIn', status: 'pending' },
    { id: 'ads_detection', label: 'Analiza reklam LinkedIn', status: 'pending' },
    { id: 'website_scan', label: 'Skanowanie strony www', status: 'pending' },
    { id: 'employee_analysis', label: 'Analiza pracowników', status: 'pending' },
    { id: 'ai_report', label: 'Generowanie raportu AI', status: 'pending' }
  ]);
  const [collectedData, setCollectedData] = useState<any>({});
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadSubmission();
  }, [submissionId]);

  const loadSubmission = async () => {
    const { data, error } = await supabase
      .from('social_selling_submissions')
      .select('*')
      .eq('id', submissionId)
      .single();

    if (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się pobrać zgłoszenia",
        variant: "destructive"
      });
      return;
    }

    setSubmission(data);
  };

  const updateStep = (stepId: string, status: ProcessStep['status'], data?: any, error?: string) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status, data, error } : step
    ));
  };

  const startAudit = async () => {
    setGenerating(true);
    setShowDataPreview(false);
    
    try {
      // Step 1: Init
      updateStep('init', 'loading');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStep('init', 'success', { domains: [submission.user_domain, ...(submission.competitor_domains || [])] });
      setCurrentStep(1);

      // Step 2: Find competitors (if not provided)
      if (!submission.competitor_domains || submission.competitor_domains.length === 0) {
        updateStep('competitors', 'loading');
        const { data: competitorData, error: competitorError } = await supabase.functions.invoke('find-competitors', {
          body: { domain: submission.user_domain }
        });

        if (competitorError) throw competitorError;
        
        updateStep('competitors', 'success', competitorData);
        setCollectedData(prev => ({ ...prev, competitors: competitorData }));
        setCurrentStep(2);
      } else {
        updateStep('competitors', 'success', { 
          competitors: submission.competitor_domains.map((d: string) => ({ domain: d })) 
        });
        setCurrentStep(2);
      }

      // Step 3: LinkedIn scraping
      updateStep('linkedin_scraping', 'loading');
      const allDomains = [submission.user_domain, ...(submission.competitor_domains || [])];
      const linkedInData: any[] = [];
      
      for (const domain of allDomains) {
        try {
          const { data: linkedinData, error: linkedinError } = await supabase.functions.invoke('scrape-linkedin-company', {
            body: { 
              linkedInUrl: `https://www.linkedin.com/company/${domain.replace(/\./g, '-')}`,
              companyName: domain
            }
          });

          if (!linkedinError && linkedinData) {
            linkedInData.push({ domain, data: linkedinData });
          }
        } catch (err) {
          console.error(`LinkedIn scraping error for ${domain}:`, err);
        }
      }

      updateStep('linkedin_scraping', 'success', linkedInData);
      setCollectedData(prev => ({ ...prev, linkedInData }));
      setCurrentStep(3);

      // Step 4: Ads detection
      updateStep('ads_detection', 'loading');
      const adsData: any[] = [];
      
      for (const domain of allDomains) {
        try {
          const { data: ads, error: adsError } = await supabase.functions.invoke('detect-linkedin-ads', {
            body: { domain }
          });

          if (!adsError && ads) {
            adsData.push({ domain, ads });
          }
        } catch (err) {
          console.error(`Ads detection error for ${domain}:`, err);
        }
      }

      updateStep('ads_detection', 'success', adsData);
      setCollectedData(prev => ({ ...prev, adsData }));
      setCurrentStep(4);

      // Step 5: Website scan
      updateStep('website_scan', 'loading');
      const { data: websiteScan, error: websiteError } = await supabase.functions.invoke('scan-website', {
        body: { domain: submission.user_domain }
      });

      if (websiteError) throw websiteError;
      
      updateStep('website_scan', 'success', websiteScan);
      setCollectedData(prev => ({ ...prev, websiteScan }));
      setCurrentStep(5);

      // Step 6: Employee analysis
      updateStep('employee_analysis', 'loading');
      const employeeData: any[] = [];
      
      for (const domain of allDomains) {
        try {
          const linkedInUrl = `https://www.linkedin.com/company/${domain.replace(/\./g, '-')}`;
          const { data: employees, error: employeeError } = await supabase.functions.invoke('scrape-employee-posts', {
            body: { 
              companyName: domain,
              domain,
              linkedInUrl
            }
          });

          if (!employeeError && employees?.data) {
            employeeData.push({ domain, data: employees.data });
          }
        } catch (err) {
          console.error(`Employee analysis error for ${domain}:`, err);
        }
      }
      
      updateStep('employee_analysis', 'success', employeeData);
      setCollectedData(prev => ({ ...prev, employeeData }));
      setCurrentStep(6);

      // Show data preview before generating AI report
      setShowDataPreview(true);
      setGenerating(false);

    } catch (error: any) {
      console.error('Audit process error:', error);
      const failedStep = steps[currentStep];
      updateStep(failedStep.id, 'error', null, error.message);
      
      toast({
        title: "Błąd podczas audytu",
        description: error.message,
        variant: "destructive"
      });
      setGenerating(false);
    }
  };

  const generateAIReport = async () => {
    setGenerating(true);
    updateStep('ai_report', 'loading');

    try {
      // Call the main audit function which will use collected data
      const { data, error } = await supabase.functions.invoke('audit-social-selling', {
        body: { submissionId }
      });

      if (error) throw error;

      updateStep('ai_report', 'success', data);
      
      toast({
        title: "Sukces!",
        description: "Raport został wygenerowany",
      });

      onComplete();
    } catch (error: any) {
      updateStep('ai_report', 'error', null, error.message);
      toast({
        title: "Błąd",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  if (!submission) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Ładowanie zgłoszenia...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Audyt Social Selling</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {submission.user_domain}
              </p>
            </div>
            <Button variant="outline" onClick={onCancel}>
              Anuluj
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Postęp</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Email:</span>
                <p className="font-medium">{submission.email}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Telefon:</span>
                <p className="font-medium">{submission.phone}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Konkurencja:</span>
                <p className="font-medium">
                  {submission.competitor_domains?.length || 0} domen
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <Badge variant="secondary">{submission.status}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Kroki procesu</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`flex items-start gap-4 p-4 rounded-lg border ${
                  step.status === 'success' ? 'bg-green-50 border-green-200' :
                  step.status === 'loading' ? 'bg-blue-50 border-blue-200' :
                  step.status === 'error' ? 'bg-red-50 border-red-200' :
                  'bg-muted/30'
                }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {step.status === 'loading' && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
                  {step.status === 'success' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                  {step.status === 'error' && <AlertCircle className="h-5 w-5 text-red-600" />}
                  {step.status === 'pending' && (
                    <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{step.label}</p>
                    {step.status === 'success' && step.data && (
                      <Badge variant="outline" className="text-xs">
                        Dane zebrane
                      </Badge>
                    )}
                  </div>
                  {step.error && (
                    <p className="text-sm text-red-600 mt-1">{step.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!showDataPreview && !generating && currentStep === 0 && (
            <Button 
              onClick={startAudit}
              className="w-full mt-6"
              size="lg"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Rozpocznij zbieranie danych
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Data Preview */}
      {showDataPreview && (
        <Card>
          <CardHeader>
            <CardTitle>Zebrane dane - podgląd</CardTitle>
            <p className="text-sm text-muted-foreground">
              Sprawdź zebrane dane przed wygenerowaniem raportu AI
            </p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="overview">Przegląd</TabsTrigger>
                <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
                <TabsTrigger value="posts">Posty firmy</TabsTrigger>
                <TabsTrigger value="employees">Pracownicy</TabsTrigger>
                <TabsTrigger value="ads">Reklamy</TabsTrigger>
                <TabsTrigger value="website">Strona WWW</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-8 w-8 text-primary" />
                        <div>
                          <p className="text-sm text-muted-foreground">Firmy</p>
                          <p className="text-2xl font-bold">
                            {(collectedData.linkedInData?.length || 0)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-8 w-8 text-primary" />
                        <div>
                          <p className="text-sm text-muted-foreground">Reklamy</p>
                          <p className="text-2xl font-bold">
                            {collectedData.adsData?.reduce((sum: number, item: any) => 
                              sum + (item.ads?.adCount || 0), 0) || 0}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Globe className="h-8 w-8 text-primary" />
                        <div>
                          <p className="text-sm text-muted-foreground">Insight Tag</p>
                          <p className="text-lg font-bold">
                            {collectedData.websiteScan?.hasInsightTag ? '✓' : '✗'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="linkedin">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {collectedData.linkedInData?.map((item: any, idx: number) => (
                    <Card key={idx}>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium text-lg">{item.domain}</p>
                            <p className="text-sm text-muted-foreground">{item.data?.name}</p>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-muted-foreground">Obserwujący</p>
                              <p className="font-medium">{item.data?.followerCount?.toLocaleString() || 0}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Pracownicy</p>
                              <p className="font-medium">{item.data?.employeeCount?.toLocaleString() || 0}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Częstotliwość</p>
                              <p className="font-medium">{item.data?.postingFrequency || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Śr. zaangażowanie</p>
                              <p className="font-medium">{item.data?.avgEngagement || 0}</p>
                            </div>
                          </div>
                          {item.data?.tagline && (
                            <div>
                              <p className="text-sm text-muted-foreground">Tagline</p>
                              <p className="text-sm">{item.data.tagline}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="posts">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {collectedData.linkedInData?.map((item: any, idx: number) => (
                    <div key={idx}>
                      <h3 className="font-medium mb-3">{item.domain}</h3>
                      {item.data?.recentPosts?.length > 0 ? (
                        <div className="space-y-2">
                          {item.data.recentPosts.slice(0, 5).map((post: any, pIdx: number) => (
                            <Card key={pIdx}>
                              <CardContent className="p-3">
                                <div className="flex items-start gap-3">
                                  <Badge variant="outline" className="text-xs">{post.type}</Badge>
                                  <div className="flex-1">
                                    <p className="text-sm line-clamp-2">{post.content}</p>
                                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                                      <span>👍 {post.likes}</span>
                                      <span>💬 {post.comments}</span>
                                      <span>🔄 {post.shares}</span>
                                      {post.ctaPresent && <Badge variant="secondary" className="text-xs">CTA</Badge>}
                                    </div>
                                    {post.hashtags?.length > 0 && (
                                      <div className="flex gap-1 mt-1 flex-wrap">
                                        {post.hashtags.slice(0, 3).map((tag: string, tIdx: number) => (
                                          <span key={tIdx} className="text-xs text-primary">{tag}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                          {item.data?.formatDiversity && (
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-sm font-medium mb-2">Różnorodność formatów</p>
                                <div className="flex gap-3 text-xs">
                                  <span>📝 Text: {item.data.formatDiversity.text}</span>
                                  <span>🖼️ Image: {item.data.formatDiversity.image}</span>
                                  <span>🎥 Video: {item.data.formatDiversity.video}</span>
                                  <span>📰 Article: {item.data.formatDiversity.article}</span>
                                  <span>📊 Poll: {item.data.formatDiversity.poll}</span>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Brak postów</p>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="employees">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {collectedData.employeeData?.map((item: any, idx: number) => (
                    <div key={idx}>
                      <h3 className="font-medium mb-3">{item.domain}</h3>
                      {item.data ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-xs text-muted-foreground">Przeanalizowanych</p>
                                <p className="text-lg font-bold">{item.data.totalAnalyzed}</p>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-xs text-muted-foreground">Kompletność profili</p>
                                <p className="text-lg font-bold">{item.data.avgProfileCompleteness}%</p>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-xs text-muted-foreground">Aktywność</p>
                                <p className="text-lg font-bold">{item.data.avgActivityScore}%</p>
                              </CardContent>
                            </Card>
                          </div>

                          {item.data.activityBreakdown && (
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-sm font-medium mb-2">Podział aktywności</p>
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                  <div>
                                    <p className="text-muted-foreground">Aktywni (≥2/mies)</p>
                                    <p className="font-medium text-green-600">{item.data.activityBreakdown.activePublishers}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Okazjonalni</p>
                                    <p className="font-medium text-yellow-600">{item.data.activityBreakdown.occasionalPublishers}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Nieaktywni</p>
                                    <p className="font-medium text-gray-600">{item.data.activityBreakdown.inactive}</p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {item.data.contentThemes && (
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-sm font-medium mb-2">Tematyka treści</p>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span>Aktualności firmowe</span>
                                    <span className="font-medium">{item.data.contentThemes.companyNews}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Thought leadership</span>
                                    <span className="font-medium">{item.data.contentThemes.thoughtLeadership}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Prezentacja produktów</span>
                                    <span className="font-medium">{item.data.contentThemes.productShowcase}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Rekrutacja</span>
                                    <span className="font-medium">{item.data.contentThemes.recruitment}%</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {item.data.topPerformers?.length > 0 && (
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-sm font-medium mb-2">Top Performerzy</p>
                                <div className="space-y-2">
                                  {item.data.topPerformers.slice(0, 3).map((performer: any, pIdx: number) => (
                                    <div key={pIdx} className="flex items-center justify-between text-xs border-b pb-2 last:border-0">
                                      <div>
                                        <p className="font-medium">{performer.name}</p>
                                        <p className="text-muted-foreground">{performer.position}</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="font-medium">{performer.postCount} postów</p>
                                        <p className="text-muted-foreground">Śr. {performer.avgEngagement} zaangażowań</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {item.data.recentPosts?.length > 0 && (
                            <Card>
                              <CardContent className="p-3">
                                <p className="text-sm font-medium mb-2">Ostatnie posty pracowników</p>
                                <div className="space-y-2">
                                  {item.data.recentPosts.slice(0, 3).map((post: any, pIdx: number) => (
                                    <div key={pIdx} className="text-xs border-b pb-2 last:border-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium">{post.author}</span>
                                        <Badge variant="outline" className="text-xs">{post.type}</Badge>
                                        {post.isCompanyRelated && <Badge variant="secondary" className="text-xs">Związane z firmą</Badge>}
                                      </div>
                                      <p className="text-muted-foreground line-clamp-2">{post.content}</p>
                                      <div className="flex gap-3 mt-1">
                                        <span>👍 {post.likes}</span>
                                        <span>💬 {post.comments}</span>
                                        <span>🔄 {post.shares}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Brak danych o pracownikach</p>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="ads">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {collectedData.adsData?.map((item: any, idx: number) => (
                    <Card key={idx}>
                      <CardContent className="p-4">
                        <p className="font-medium mb-2">{item.domain}</p>
                        <p className="text-sm text-muted-foreground">
                          Znalezionych reklam: {item.ads?.adCount || 0}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="website">
                <Card>
                  <CardContent className="p-4">
                    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                      {JSON.stringify(collectedData.websiteScan, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex gap-3 mt-6">
              <Button 
                onClick={generateAIReport}
                disabled={generating}
                className="flex-1"
                size="lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Generowanie raportu AI...
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-5 w-5" />
                    Wygeneruj raport AI
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};